import { ModalContext } from 'app/app';
import axios from 'axios';
import CustomLoader from 'components/CustomLoader';
import { AuthContext, ViewContext, useNavigate } from 'components/lib';
import InputUtils from 'helpers/inputUtils';
import { Fragment, useContext, useEffect, useState } from 'react';
import { classOptions, stateOptions, trailersBodytype, vehiclesBodytype } from 'data';

// Components
import { SelectPolicy } from './components/SelectPolicy';
import { SelectEndorsementType } from './components/SelectEndorsementType';
import { AddVehicleOrTrailer } from './components/AddVehicleOrTrailer';
import { AddDriver } from './components/AddDriver';
import { RemoveDriver } from './components/RemoveDriver';
import { RemoveVehicleOrTrailer } from './components/RemoveVehicleOrTrailer';
import { AdditionalInterest } from './components/AdditionalInterest';
import { OtherEndorsement } from './components/OtherEndorsement';

import {
  add_driver_validation,
  add_vehicle_or_trailer_validation,
  additional_interest_validation,
  other_validation
} from './validationSchemas';

import styles from './styles.module.scss';

export function EndorsementModalV2() {
  const { user } = useContext(AuthContext);
  const context = useContext(ViewContext);
  const navigate = useNavigate();
  const { modal, setModal } = useContext(ModalContext);

  const [stage, setStage] = useState('select_policy');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles_and_trailers, setVehicles_and_trailers] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [changes, setChanges] = useState([]);
  const [stated_value, setStatedValue] = useState('');
  const [accountInfo, setAccountInfo] = useState(null);

  async function fetchAccountInfo() {
    try {
      const response = await axios.get('/api/account');
      if (response.data && response.data.data) {
        setAccountInfo(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching account info:", error);
      context.notification.show(
        'Failed to load account information.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }

  async function submit(endorsements) {
    if (endorsements.length === 0) return;

    setSubmitting(true);

    try {
      const formattedChanges = endorsements.map(change => {
        if (
          change.type === 'remove_drivers' || change.type === 'remove_vehicles_or_trailers'
        ) {
          return {
            ...change,
            value: change.value.map(item => {
              const { actions, ...cleanItem } = item;
              return cleanItem;
            })
          };
        }
        return change;
      });

      const quoteRequest = selected?.selected_policy?.quote_request;
      const formattedQuoteRequest =
        typeof quoteRequest === 'string'
          ? quoteRequest === 'true'
          : Boolean(quoteRequest);

      const response = await axios.post('/api/endorsement', {
        cc_policy_id: selected?.selected_policy?.cc_policy_id,
        policy_number: selected?.selected_policy?.policy_number,
        policy_status: selected?.selected_policy?.policy_status,
        source: selected?.selected_policy?.source,
        agency_id: selected?.selected_policy?.agency_id,
        company_id: selected?.selected_policy?.company_id,
        company_name: selected?.selected_policy?.company_name,
        account_id: user.account_id,
        account_name: accountInfo?.name || selected?.selected_policy?.account_name || "",
        agent_email: accountInfo?.owner_email || user.email || "",
        insured_name: selected?.selected_policy?.insured_name,
        quote_request: formattedQuoteRequest,
        changes: formattedChanges,
      });

      if (response.data.success) {
        setSubmitting(false);
        setModal({});
        setSelected(null);
        setDrivers([]);
        setVehicles_and_trailers([]);
        setEndorsements([]);
        setChanges([]);
        setStatedValue('');
        setStage('select_policy');

        context.notification.show(
          'Endorsement created successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );

        if (response.data) {
          navigate(`/app/endorsement/view/${response.data.data.id}`);
        }
      }
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      setModal({});
      setSelected(null);
      setDrivers([]);
      setVehicles_and_trailers([]);
      setEndorsements([]);
      setChanges([]);
      setStatedValue('');
      setStage('select_policy');

      context.notification.show(
        'Failed to create endorsement.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }

  useEffect(() => {
    fetchAccountInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mappedChanges = [];

    for (var i = 0; i < endorsements.length; i++) {
      if (endorsements[i].type === 'add_vehicle_or_trailer') {
        mappedChanges.push(
          `Added a Vehicle/Trailer: ${endorsements[i].value.make}
          ${endorsements[i].value.model} ${endorsements[i].value.vin}`
        );
      }

      if (endorsements[i].type === 'add_driver') {
        mappedChanges.push(
          `Added a Driver: ${endorsements[i].value.first_name} ${endorsements[i].value.last_name}`
        );
      }

      if (endorsements[i].type === 'remove_drivers') {
        endorsements[i].value.forEach((removed) => {
          const label = removed.description ?? removed.Name ?? '';
          mappedChanges.push(`Removed a Driver: ${label}`);
        });
      }

      if (endorsements[i].type === 'remove_vehicles_or_trailers') {
        endorsements[i].value.forEach((removed) => {
          const label = removed.description ?? ((`${removed.Make ?? ''} ${removed.Model ?? ''}`.trim()) || '—');
          mappedChanges.push(`Removed a Vehicle/Trailer: ${label}`);
        });
      }


      if (endorsements[i].type === 'additional_interest') {
        mappedChanges.push(
          `Additional Interest: ${endorsements[i].value.name_of_interest}`
        );
      }

      if (endorsements[i].type === 'other_endorsement') {
        mappedChanges.push(
          `Other Endorsement: ${endorsements[i].value.length > 50
            ? endorsements[i].value.slice(0, 47) + '...'
            : endorsements[i].value
          }`
        );
      }

      setChanges(
        mappedChanges.map((change, index) => {
          return {
            'Quantity of changes': `Change #${index + 1}`,
            'What has changed': change,
          };
        })
      );
    }
  }, [endorsements]);

  function resetModal() {
    setSubmitting(false);
    setModal({});
    setSelected(null);
    setDrivers([]);
    setVehicles_and_trailers([]);
    setEndorsements([]);
    setChanges([]);
    setStatedValue('');
    setStage('select_policy');
  }

  const input_formatter = new InputUtils();
  const handleChangeStatedValue = (event, handleChange) => {
    handleChange(event);
    event.target.value = event.target.value
      .replaceAll(/\D/g, '')
      .replace(/\?.*/g, ".'");
    setStatedValue(input_formatter.currency(event.target.value));
  };

  return (
    <Fragment>
      {modal.name === 'endorsement_modal_v2' && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            {loading ? (
              <div
                className={styles.insideWrapper}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CustomLoader />
              </div>
            ) : (
              <>
                {stage === 'select_policy' && (
                  <SelectPolicy
                    setModal={setModal}
                    setStage={setStage}
                    setSelected={setSelected}
                    setDrivers={setDrivers}
                    setVehicles_and_trailers={setVehicles_and_trailers}
                    accountInfo={accountInfo}
                  />
                )}

                {stage === 'select_endorsement_type' && (
                  <SelectEndorsementType
                    setStage={setStage}
                    resetModal={resetModal}
                  />
                )}

                {stage === 'add_vehicle_or_trailer' && (
                  <AddVehicleOrTrailer
                    validation={add_vehicle_or_trailer_validation}
                    endorsements={endorsements}
                    changes={changes}
                    loading={loading}
                    stated_value={stated_value}
                    handleChangeStatedValue={handleChangeStatedValue}
                    setEndorsements={setEndorsements}
                    setStage={setStage}
                    submit={submit}
                    submitting={submitting}
                    resetModal={resetModal}
                    classOptions={classOptions}
                    vehiclesBodytype={vehiclesBodytype}
                    trailersBodytype={trailersBodytype}
                  />
                )}

                {stage === 'add_driver' && (
                  <AddDriver
                    validation={add_driver_validation}
                    endorsements={endorsements}
                    changes={changes}
                    loading={loading}
                    setEndorsements={setEndorsements}
                    setStage={setStage}
                    submit={submit}
                    submitting={submitting}
                    resetModal={resetModal}
                    stateOptions={stateOptions}
                  />
                )}

                {stage === 'remove_driver' && (
                  <RemoveDriver
                    drivers={drivers}
                    setDrivers={setDrivers}
                    endorsements={endorsements}
                    changes={changes}
                    loading={loading}
                    selected={selected}
                    carrier={selected?.selected_policy?.carrier}
                    setEndorsements={setEndorsements}
                    setStage={setStage}
                    submit={submit}
                    submitting={submitting}
                    resetModal={resetModal}
                  />
                )}

                {stage === 'remove_vehicle_or_trailer' && (
                  <RemoveVehicleOrTrailer
                    vehicles_and_trailers={vehicles_and_trailers}
                    setVehicles_and_trailers={setVehicles_and_trailers}
                    endorsements={endorsements}
                    changes={changes}
                    loading={loading}
                    selected={selected}
                    carrier={selected?.selected_policy?.carrier}
                    setEndorsements={setEndorsements}
                    setStage={setStage}
                    submit={submit}
                    submitting={submitting}
                    resetModal={resetModal}
                  />
                )}

                {stage === 'additional_interest' && (
                  <AdditionalInterest
                    validation={additional_interest_validation}
                    endorsements={endorsements}
                    changes={changes}
                    loading={loading}
                    setEndorsements={setEndorsements}
                    setStage={setStage}
                    submit={submit}
                    submitting={submitting}
                    resetModal={resetModal}
                  />
                )}

                {stage === 'other_endorsement' && (
                  <OtherEndorsement
                    validation={other_validation}
                    endorsements={endorsements}
                    changes={changes}
                    loading={loading}
                    setEndorsements={setEndorsements}
                    setStage={setStage}
                    submit={submit}
                    submitting={submitting}
                    resetModal={resetModal}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
}
